import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User, Settings, LogOut, Menu, Target, List, BarChart3, Calendar, Clock, Bell, LightbulbIcon, Cloud, FileText, FileStack, HelpCircle, Shield, MessageSquare, UserPlus } from 'lucide-react';
import { Button } from '../ui/Button';
import { Stack } from './Stack';
import { useNavigationStore } from '@/store/navigationStore';
import { useAuthStore } from '@/store/authStore';
import { personaService } from '@/services/personaService';
import { useFeedbackStore } from '@/hooks/useFeedback';
import { SignOutConfirmModal } from '../auth/SignOutConfirmModal';

export function UserProfileDropdown() {
  const [isOpen, setIsOpen] = useState(false);
  const [showSignOutConfirm, setShowSignOutConfirm] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const { currentPageAction } = useNavigationStore();
  const { user, signOut, syncToCloud, isAuthenticated, setShowAuthModal, setAuthModalMode } = useAuthStore();
  const { openFeedback } = useFeedbackStore();

  // Initialize persona service
  personaService.initializeIfNeeded();

  // Close dropdown when clicking outside
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Close dropdown on escape key
  useEffect(() => {
    function handleEscape(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      return () => {
        document.removeEventListener('keydown', handleEscape);
      };
    }
    return undefined;
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Trigger Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={handleToggle}
        className="flex items-center justify-center h-9 w-9"
        aria-expanded={isOpen}
        aria-haspopup="menu"
        aria-label="Menu"
      >
        <Menu className="h-8 w-8" />
      </Button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="header-burger-dropdown"
          role="menu"
          aria-orientation="vertical"
        >
          {/* Page Action (if available) - only when authenticated */}
          {isAuthenticated && currentPageAction && (
            <div className="header-burger-section-bordered">
              <div className="header-burger-section-content">
                <Button
                  variant="primary"
                  size="sm"
                  className="header-burger-action-btn"
                  onClick={() => {
                    handleClose();
                    currentPageAction.onClick();
                  }}
                  role="menuitem"
                >
                  <currentPageAction.icon className="icon-with-text" />
                  {currentPageAction.label}
                </Button>
              </div>
            </div>
          )}

          {/* Sign In / Sign Up - only when not authenticated */}
          {!isAuthenticated && (
            <div className="header-burger-section-bordered">
              <div className="header-burger-section-content">
                <Stack spacing="xs">
                  <Button
                    variant="primary"
                    size="sm"
                    className="header-burger-action-btn"
                    onClick={() => {
                      handleClose();
                      setAuthModalMode('signin');
                      setShowAuthModal(true);
                    }}
                    role="menuitem"
                  >
                    <User className="icon-with-text" />
                    Sign In
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    className="header-burger-action-btn"
                    onClick={() => {
                      handleClose();
                      setAuthModalMode('signup');
                      setShowAuthModal(true);
                    }}
                    role="menuitem"
                  >
                    <UserPlus className="icon-with-text" />
                    Create Account
                  </Button>
                </Stack>
              </div>
            </div>
          )}

          {/* Navigation Items - only when authenticated */}
          {isAuthenticated && (
            <div className="header-burger-section">
              <Stack spacing="xs" className="header-burger-section-content">
                <Link to="/" role="menuitem" onClick={handleClose} tabIndex={-1}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="header-burger-menu-item"
                    onClick={() => window.location.href = '/'}
                  >
                    <span>Aspirations</span>
                    <LightbulbIcon className="icon-md" />
                  </Button>
                </Link>
                <Link to="/goals" role="menuitem" onClick={handleClose} tabIndex={-1}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="header-burger-menu-item"
                    onClick={() => window.location.href = '/goals'}
                  >
                    <span>Goals</span>
                    <Target className="icon-md" />
                  </Button>
                </Link>

                <Link to="/lists" role="menuitem" onClick={handleClose} tabIndex={-1}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="header-burger-menu-item"
                    onClick={() => window.location.href = '/lists'}
                  >
                    <span>Lists</span>
                    <List className="icon-md" />
                  </Button>
                </Link>

                <Link to="/list-templates" role="menuitem" onClick={handleClose} tabIndex={-1}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="header-burger-menu-item"
                    onClick={() => window.location.href = '/list-templates'}
                  >
                    <span>List Templates</span>
                    <FileStack className="icon-md" />
                  </Button>
                </Link>

                <Link to="/schedules" role="menuitem" onClick={handleClose} tabIndex={-1}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="header-burger-menu-item"
                    onClick={() => window.location.href = '/schedules'}
                  >
                    <span>Schedules</span>
                    <Clock className="icon-md" />
                  </Button>
                </Link>

                <Link to="/calendar" role="menuitem" onClick={handleClose} tabIndex={-1}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="header-burger-menu-item"
                    onClick={() => window.location.href = '/calendar'}
                  >
                    <span>Calendar</span>
                    <Calendar className="icon-md" />
                  </Button>
                </Link>
                <Link to="/projections" role="menuitem" onClick={handleClose} tabIndex={-1}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="header-burger-menu-item"
                    onClick={() => window.location.href = '/projections'}
                  >
                    <span>Projections</span>
                    <BarChart3 className="icon-md" />
                  </Button>
                </Link>
                <Link to="/reports" role="menuitem" onClick={handleClose} tabIndex={-1}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="header-burger-menu-item"
                    onClick={() => window.location.href = '/reports'}
                  >
                    <span>Reports</span>
                    <FileText className="icon-md" />
                  </Button>
                </Link>
              </Stack>
            </div>
          )}

          {/* User Settings - authenticated items */}
          {isAuthenticated && (
            <div className="header-burger-section-top-bordered">
              <Stack spacing="xs" className="header-burger-section-content">
                <Link to="/notifications" role="menuitem" onClick={handleClose} tabIndex={-1}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="header-burger-menu-item"
                    onClick={() => window.location.href = '/notifications'}
                  >
                    <span>Notifications</span>
                    <Bell className="icon-md" />
                  </Button>
                </Link>
                <Link to="/profile" role="menuitem" onClick={handleClose} tabIndex={-1}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="header-burger-menu-item"
                    onClick={() => window.location.href = '/profile'}
                  >
                    <span>Profile</span>
                    <User className="icon-md" />
                  </Button>
                </Link>

                <Link to="/settings" role="menuitem" onClick={handleClose} tabIndex={-1}>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="header-burger-menu-item"
                    onClick={() => window.location.href = '/settings'}
                  >
                    <span>Settings</span>
                    <Settings className="icon-md" />
                  </Button>
                </Link>
              </Stack>
            </div>
          )}

          {/* Public items - always visible */}
          <div className={isAuthenticated ? "header-burger-section-top-bordered" : "header-burger-section"}>
            <Stack spacing="xs" className="header-burger-section-content">
              <Link to="/help" role="menuitem" onClick={handleClose} tabIndex={-1}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="header-burger-menu-item"
                  onClick={() => window.location.href = '/help'}
                >
                  <span>Help</span>
                  <HelpCircle className="icon-md" />
                </Button>
              </Link>
              <Button
                variant="ghost"
                size="sm"
                className="header-burger-menu-item"
                onClick={() => {
                  handleClose();
                  openFeedback();
                }}
                role="menuitem"
              >
                <span>Send Feedback</span>
                <MessageSquare className="icon-md" />
              </Button>
              <Link to="/privacy-policy" role="menuitem" onClick={handleClose} tabIndex={-1}>
                <Button
                  variant="ghost"
                  size="sm"
                  className="header-burger-menu-item"
                  onClick={() => window.location.href = '/privacy-policy'}
                >
                  <span>Privacy Policy</span>
                  <Shield className="icon-md" />
                </Button>
              </Link>
            </Stack>
          </div>

          {/* User Info Section (if authenticated) */}
          {isAuthenticated && user && (
            <div className="header-burger-user-section">
              <div className="header-burger-user-info">
                <div className="header-burger-user-avatar">
                  <span className="header-burger-user-avatar-text">
                    {user.name.charAt(0).toUpperCase()}
                  </span>
                </div>
                <div className="header-burger-user-details">
                  <p className="header-burger-user-name">{user.name}</p>
                  <p className="header-burger-user-email">{user.email}</p>
                </div>
              </div>

              {/* Quick sync button if cloud sync is enabled */}
              {user.preferences.enableCloudSync && (
                <Button
                  variant="outline"
                  size="sm"
                  className="header-burger-sync-btn"
                  onClick={() => {
                    handleClose();
                    syncToCloud().catch(console.error);
                  }}
                >
                  <Cloud className="icon-sm mr-1" />
                  Sync Now
                </Button>
              )}
            </div>
          )}

          {/* Logout Section */}
          {isAuthenticated && (
            <div className="header-burger-section-top-bordered">
              <div className="header-burger-section-content">
                <Button
                  variant="ghost"
                  size="sm"
                  className="header-burger-logout-btn"
                  onClick={() => {
                    handleClose();
                    setShowSignOutConfirm(true);
                  }}
                  role="menuitem"
                >
                  <span>Sign Out</span>
                  <LogOut className="icon-md" />
                </Button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Sign Out Confirmation Modal */}
      <SignOutConfirmModal
        isOpen={showSignOutConfirm}
        onClose={() => setShowSignOutConfirm(false)}
        onConfirm={signOut}
      />
    </div>
  );
}